/**
 * PWA Service Worker Hook
 * Handles SW registration, updates, and offline status
 */
import { useEffect, useState, useCallback, useSyncExternalStore } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

interface PWAStatus {
  /** Whether the app needs to refresh to update */
  needRefresh: boolean;
  /** Whether the user is currently offline */
  offlineReady: boolean;
  /** Whether the service worker is being updated */
  isUpdating: boolean;
  /** Error during registration, if any */
  registrationError: Error | null;
}

interface UsePWAReturn extends PWAStatus {
  /** Call this to accept the update and refresh */
  updateServiceWorker: () => void;
  /** Call this to dismiss the update prompt */
  dismissUpdate: () => void;
  /** Whether the app is running as installed PWA */
  isInstalled: boolean;
  /** Whether the device supports installation */
  canInstall: boolean;
  /** Trigger the install prompt (if available) */
  promptInstall: () => Promise<void>;
}

// Store the beforeinstallprompt event for later use
let deferredPrompt: BeforeInstallPromptEvent | null = null;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const STANDALONE_MEDIA_QUERY = '(display-mode: standalone)';

/**
 * Subscribe to display-mode changes so consumers re-render when the user
 * accepts Chrome's in-app install dialog mid-session.
 *
 * iOS Safari's `navigator.standalone` is read in getStandaloneSnapshot but
 * never emits an event; iOS launches the installed app as a fresh document
 * load, so a missed mid-session flip self-corrects on the next page load.
 */
function subscribeToStandaloneChange(callback: () => void): () => void {
  const mql = window.matchMedia(STANDALONE_MEDIA_QUERY);
  mql.addEventListener('change', callback);
  return () => {
    mql.removeEventListener('change', callback);
  };
}

function getStandaloneSnapshot(): boolean {
  return (
    window.matchMedia(STANDALONE_MEDIA_QUERY).matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  );
}

function getServerSnapshot(): boolean {
  return false;
}

/**
 * Hook to manage PWA service worker lifecycle
 * Provides update prompts, offline status, and install functionality
 */
export function usePWA(): UsePWAReturn {
  const [canInstall, setCanInstall] = useState(false);
  const [registrationError, setRegistrationError] = useState<Error | null>(
    null
  );

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      // Check for updates periodically (every hour)
      if (registration) {
        setInterval(
          () => {
            registration.update().catch((err) => {
              console.warn('SW update check failed:', err);
            });
          },
          60 * 60 * 1000
        ); // 1 hour
      }
      console.log(`Service Worker registered: ${swUrl}`);
    },
    onRegisterError(error) {
      console.error('Service Worker registration error:', error);
      setRegistrationError(error);
    },
  });

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      deferredPrompt = null;
      setCanInstall(false);
      console.log('SabiBooks was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const dismissUpdate = useCallback(() => {
    setOfflineReady(false);
    setNeedRefresh(false);
  }, [setOfflineReady, setNeedRefresh]);

  const promptInstall = useCallback(async () => {
    const prompt = deferredPrompt;
    if (prompt === null) {
      return;
    }

    await prompt.prompt();
    const { outcome } = await prompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    deferredPrompt = null;
    setCanInstall(false);
  }, []);

  // Reactive: re-renders when (display-mode: standalone) flips during the
  // session (e.g., user accepts Chrome's in-app install dialog).
  const isInstalled = useSyncExternalStore(
    subscribeToStandaloneChange,
    getStandaloneSnapshot,
    getServerSnapshot,
  );

  return {
    needRefresh,
    offlineReady,
    isUpdating: false,
    registrationError,
    updateServiceWorker: () => updateServiceWorker(true),
    dismissUpdate,
    isInstalled,
    canInstall,
    promptInstall,
  };
}

/**
 * Simple hook to track online/offline status.
 *
 * @deprecated Use `useOnlineStatus` from '@/hooks/useOnlineStatus' for enhanced
 * functionality including ping verification, debouncing, and manual checks.
 */
export function useOnlineStatusSimple(): boolean {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
